import { Table as BaseTable, FlexMiddle, CircleIcon, FormModal as BaseFormModal } from '@ubeya/shared-web/components';
import { ReactComponent as TrashIcon } from '@ubeya/shared-web/assets/delete.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import React, { useContext, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { isSafari } from 'react-device-detect';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { SORT_BY_DATE_AND_TIME, SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import { COMPASS_DE_ORGANIZATION_ID } from '@ubeya/shared/utils/accounts';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { PTO_TYPES } from '@ubeya/shared/constants';
import { Trans } from 'react-i18next';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';

const FormModal = styled(BaseFormModal)`
  .submit-button:not(:disabled) {
    background: ${({ theme }) => theme.colors.redNegative} !important;
  }
`;

const Table = styled(BaseTable)`
  ${({ $isSafari }) =>
    $isSafari
      ? css`
          max-width: 90%;
        `
      : css`
          width: 100%;
        `}

  .row.disabled {
    background: ${({ theme }) => theme.colors.white};
    > .cell {
      cursor: default;
    }
  }
  .row:hover {
    svg {
      visibility: visible;
    }
  }
`;

const Wrapper = styled(FlexMiddle)`
  min-height: 36px;
  justify-content: flex-end;
  margin-right: -10px;
`;

const SORT_MAP = {
  date: SORT_BY_DATE_AND_TIME('date'),
  type: SORT_BY_STRING('type')
};

const DeletePtoConfirmationModal = ({ pto, onConfirm, onClose }) => {
  const { t } = useContext(ConfigContext);
  const { addResponseError } = useToaster();

  const handleConfirm = async () => {
    if (!pto.id) return;
    try {
      await onConfirm(pto.id);
      trackEvent('Leave management - deleted PTO', { module: 'Leave Management', ptoType: pto.type });
    } catch (error) {
      addResponseError(error);
    } finally {
      onClose();
    }
  };

  const modalTitle =
    pto.type === PTO_TYPES.Holiday ? t('ContractTypes_deletePTO_title') : t('ContractTypes_deleteSickday_title');

  const modalDescription =
    pto.type === PTO_TYPES.Holiday ? (
      <Trans
        i18nKey="ContractTypes_deletePTO_text"
        components={{
          br: <br />
        }}
      />
    ) : (
      <Trans
        i18nKey="ContractTypes_deleteSickday_text"
        components={{
          br: <br />
        }}
      />
    );

  return (
    <FormModal
      onSubmit={handleConfirm}
      size="small"
      onClose={onClose}
      title={modalTitle}
      confirmText={t('delete')}
      showCancelButton
      cancelButtonProps={{ secondary: true }}
      disabledOnPristine={false}>
      <p>{modalDescription}</p>
    </FormModal>
  );
};

const PtoActionRenderer = ({ rowData, columnData: { onOpenDeleteModal, hasActionPermission } }) => {
  const { t, organizationId } = useContext(ConfigContext);
  const isCompassDE = organizationId === COMPASS_DE_ORGANIZATION_ID;

  const canDelete = (!rowData.payrollProcessId || isCompassDE) && hasActionPermission;

  return (
    <Wrapper>
      {canDelete && <CircleIcon icon={TrashIcon} tooltip={t('delete')} onClick={() => onOpenDeleteModal(rowData)} />}
    </Wrapper>
  );
};

const PtoLogsTable = ({ ptoData, onDeletePto, hasActionPermission }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  const { mappedPtoTypes } = usePtoTypes();
  const [ptoToDelete, setPtoToDelete] = useState(null);
  const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'desc' });
  const sorter = SORT_MAP[sort.sortBy];

  const handleOpenDeleteModal = (pto) => {
    setPtoToDelete(pto);
  };

  const ptoLogsData = useMemo(() => {
    const ptos = ptoData?.ptos || [];
    return [...ptos].sort((a, b) => (sort.sortDirection === 'asc' ? sorter(a, b) : sorter(b, a)));
  }, [ptoData?.ptos, sort.sortDirection, sorter]);

  const ptoLogsColumns = useMemo(
    () => [
      {
        Header: t('date'),
        dataKey: 'date',
        formatter: (value) => formattingMap.date(value),
        sortable: true
      },
      { Header: t('type'), dataKey: 'type', formatter: (value) => mappedPtoTypes[value]?.label, sortable: true },
      { Header: t('approvedBy'), dataKey: 'approvedBy' },
      {
        Header: t('status'),
        dataKey: 'payrollProcessId',
        formatter: (value) => (value ? t('processed') : t('pendingPayroll'))
      }
    ],
    [formattingMap, mappedPtoTypes, t]
  );

  return (
    <>
      <Table
        headerRowHeight={50}
        isVirtualized={false}
        autoHeight
        data={ptoLogsData}
        columns={ptoLogsColumns}
        $isSafari={isSafari}
        sortBy={sort.sortBy}
        sortDirection={sort.sortDirection}
        setSort={setSort}
        actionRenderer={({ rowData }) => (
          <PtoActionRenderer
            rowData={rowData}
            columnData={{
              onOpenDeleteModal: handleOpenDeleteModal,
              hasActionPermission
            }}
          />
        )}
      />

      {ptoToDelete && (
        <DeletePtoConfirmationModal pto={ptoToDelete} onConfirm={onDeletePto} onClose={() => setPtoToDelete(null)} />
      )}
    </>
  );
};

export default PtoLogsTable;
